import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import { Dropdown } from 'antd';
import { snakeCase } from 'lodash';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { ChannelConfiguration, Configuration } from 'api/notifications';
import {
  DateFieldEventTriggerTypes,
  ReminderConfiguration,
  Channel,
  ReminderOption,
} from 'api/notifications/configuration';
import { Menu } from 'components/atoms/dropdown';
import {
  EmptyCheckBoxPlaceholder,
  Trigger,
} from 'components/organisms/notifications/common';
import {
  NotificationUserType,
  useEventTypeMessage,
  useNotificationContext,
} from '../../hooks';
import { ChannelOption, ChannelOptions, Title } from '../basics';
import { useChannels, empty, ChannelDisabled } from '../channels';
import {
  EventTypeItem,
  StyledList,
  StyledRadio,
  TriggerItem,
  TriggerName,
} from './shared';

export interface ReminderConfigurationProps {
  configuration: ReminderConfiguration;
  mandatory?: boolean;
  mandatoryConfig?: ChannelConfiguration;
  onChange: (configuration: Configuration) => void;
}

export const ReminderConfigurationView: FC<ReminderConfigurationProps> = ({
  configuration,
  mandatory = false,
  mandatoryConfig,
  onChange,
}) => {
  const channelEnabledMap = useChannels();
  const channels = Object.keys(channelEnabledMap) as Channel[];
  const { isCustomerPortal } = useNotificationContext();
  const userType = isCustomerPortal
    ? NotificationUserType.Customer
    : NotificationUserType.Vendor;

  return (
    <EventTypeItem>
      <Title>
        {useEventTypeMessage(configuration.eventType, { userType })}
        <Dropdown
          trigger={['click']}
          overlay={
            <Menu
              selectable
              selectedKeys={[
                configuration.meta?.type ?? ReminderOption.SameDay,
              ]}
              onSelect={({ key }) =>
                onChange({
                  ...configuration,
                  meta: { type: key as ReminderOption },
                })
              }
            >
              {Object.keys(ReminderOption)
                .map((x) => x as ReminderOption)
                .map((x) => (
                  <Menu.Item key={x}>
                    <FormattedMessage
                      id={`notifications.configuration.reminder_options.${snakeCase(
                        x,
                      )}`}
                    />
                  </Menu.Item>
                ))}
            </Menu>
          }
        >
          <Trigger>
            <FormattedMessage
              id={`notifications.configuration.reminder_options.${snakeCase(
                configuration.meta?.type ?? ReminderOption.SameDay,
              )}`}
            />
            <ChevronDown16 />
          </Trigger>
        </Dropdown>
      </Title>
      <StyledList>
        {DateFieldEventTriggerTypes.map((trigger) => (
          <StyledList.Item key={trigger}>
            <TriggerItem>
              <StyledRadio
                checked={configuration.eventTriggerType === trigger}
                onChange={() =>
                  onChange({ ...configuration, eventTriggerType: trigger })
                }
              />
              <TriggerName>
                <FormattedMessage
                  id={`notifications.configuration.date_field_triggers.${snakeCase(
                    trigger,
                  )}`}
                  values={{ userType }}
                />
              </TriggerName>
              <ChannelOptions
                value={Object.entries(configuration.channels)
                  .filter(([, value]) => !!value)
                  .map(([key]) => key)}
                onChange={(values) =>
                  onChange({
                    ...configuration,
                    channels: (values as any as Channel[]).reduce(
                      (prev, channel) => ({ ...prev, [channel]: true }),
                      empty,
                    ),
                  })
                }
              >
                {configuration.eventTriggerType === trigger
                  ? channels.map((x) =>
                      x === Channel.Slack ? (
                        <EmptyCheckBoxPlaceholder />
                      ) : (
                        <ChannelOption
                          key={x}
                          value={x}
                          disabled={
                            (mandatoryConfig
                              ? mandatoryConfig[x]
                              : mandatory) ||
                            (!channelEnabledMap[x].enabled && !isCustomerPortal)
                          }
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...(channelEnabledMap[x].enabled === false
                            ? {
                                title: (channelEnabledMap[x] as ChannelDisabled)
                                  .disabledMessage,
                              }
                            : {})}
                        />
                      ),
                    )
                  : null}
              </ChannelOptions>
            </TriggerItem>
          </StyledList.Item>
        ))}
      </StyledList>
    </EventTypeItem>
  );
};
